import {
  FloatingArrow,
  Placement,
  arrow,
  autoUpdate,
  flip,
  offset,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
  useTransitionStyles,
} from "@floating-ui/react";
import { ReactNode, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { getTooltipClassNames, TooltipVariant } from "./class-names";

type TooltipType = "default" | "error";

const delayByType: Record<TooltipType, { open: number; close: number }> = {
  default: { open: 500, close: 200 },
  error: { open: 0, close: 200 },
};

export type TooltipProps = {
  id?: string;
  children: ReactNode;
  className?: string;
  variant?: TooltipVariant;
  title?: string;
  text?: string;
  placement?: Placement;
  offsetBy?: number;
  type?: TooltipType;
  mountTo?: "body" | "parent";
};

export const Tooltip = ({
  id,
  className,
  children,
  variant = "label",
  title,
  text,
  placement = "right",
  offsetBy = 0,
  type,
  mountTo = "parent",
}: TooltipProps) => {
  const tooltipArrowRef = useRef(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const tooltip = useFloating({
    placement,
    open: tooltipOpen,
    onOpenChange: setTooltipOpen,
    middleware: [flip(), arrow({ element: tooltipArrowRef }), offset(offsetBy)],
    whileElementsMounted: autoUpdate,
  });
  const tooltipInteractions = useInteractions([
    useHover(tooltip.context, {
      move: false,
      delay: {
        open: delayByType[type || "default"].open,
        close: delayByType[type || "default"].close,
      },
    }),
    useFocus(tooltip.context),
    useDismiss(tooltip.context),
    useRole(tooltip.context, { role: "tooltip" }),
  ]);

  const { isMounted, styles: transitionStyles } = useTransitionStyles(
    tooltip.context,
    {
      close: () => ({ opacity: 0, transform: "scale(0.5)" }),
    },
  );

  const content =
    variant === "label" ? (
      <span className="text-ui">{text}</span>
    ) : (
      <>
        {title && <h4 className="text-paragraph font-semibold">{title}</h4>}
        <p className="text-ui whitespace-pre-wrap">{text}</p>
      </>
    );

  const container = (
    <div
      className="text-black/90 z-10"
      ref={tooltip.refs.setFloating}
      style={tooltip.floatingStyles}
      {...tooltipInteractions.getFloatingProps()}
    >
      <div style={transitionStyles}>
        <FloatingArrow
          ref={tooltipArrowRef}
          context={tooltip.context}
          tipRadius={2}
        />
        <div className={getTooltipClassNames({ variant, placement })}>
          {content}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div
        id={id}
        className={className}
        ref={tooltip.refs.setReference}
        {...tooltipInteractions.getReferenceProps()}
      >
        {children}
      </div>
      {!!text &&
        isMounted &&
        (mountTo === "body"
          ? createPortal(container, document.body)
          : container)}
    </>
  );
};

type TruncatedTooltipProps = Omit<TooltipProps, "children" | "text"> & {
  tooltipText?: string;
  textToDisplay: string;
  dataTestId?: string;
};

export const TruncatedTooltip = ({
  textToDisplay,
  tooltipText,
  dataTestId,
  className,
  ...rest
}: TruncatedTooltipProps) => {
  const textRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        setIsTruncated(
          textRef.current.scrollWidth > textRef.current.clientWidth,
        );
      }
    };

    checkTruncation();
  }, [textToDisplay]);

  const content = (
    <div ref={textRef} className={className} data-testid={dataTestId}>
      {textToDisplay}
    </div>
  );

  return isTruncated && tooltipText ? (
    <Tooltip text={tooltipText} {...rest}>
      {content}
    </Tooltip>
  ) : (
    content
  );
};
